import { useEffect, useReducer, useState } from "react";
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect, useHistory } from "react-router-dom";
import OrderingLayout, { OrderReceipt } from "../components/orderingLayout";
import BlikCodeInput from "../components/blikCodeInput";
import ProductsSummary from "../components/productSummary";
import SelectionTiles from "../components/selectionTiles";
import { ProductItem } from "../components/styles";
import cashIcon from "../images/cash-payment.svg";
import blikIcon from "../images/blik-logo.png";
import p24Icon from "../images/P24_logo.svg";
import cardIcon from "../images/payment.svg";
import { cartProductsSelector, totalSelector } from "../modules/cart/selectors";
import { completeCheckout } from "../modules/checkout/actions";
import { DELIVERY_ADDRESS, DELIVERY_PICKUP } from "../modules/delivery/reducer";
import {
  deliveryAvailableSelector,
  pickupLocationSelector,
} from "../modules/delivery/selectors";
import {
  currentDateSelector,
  menuReadySelector,
} from "../modules/products/selectors";
import { canCheckoutSelector } from "../modules/checkout/selectors";
import { Button } from "../styles";
import { client } from "../utils/api";
import Modal from "../components/modal";
import { rememberOrderThankyou } from "../utils/orderThankyou";
import { ArrowRight } from "lucide-react";

function Checkout() {
  const cart = useSelector(cartProductsSelector);
  const total = useSelector(totalSelector);
  const {
    address,
    cost: deliveryCost,
    quote: deliveryQuote,
    type: deliveryType,
  } = useSelector((state) => state.delivery);
  const pickupLocation = useSelector(pickupLocationSelector);
  const deliveryAvailable = useSelector(deliveryAvailableSelector);
  const checkoutData = useSelector((state) => state.checkout);
  const { paymentTypes } = useSelector((state) => state.store);
  const date = useSelector(currentDateSelector);
  const menuReady = useSelector(menuReadySelector);
  const canCheckout = useSelector(canCheckoutSelector);
  const [apiError, dispatchAPIError] = useReducer(
    (state, action) => ({ ...state, ...action }),
    { message: "", show: false },
  );
  const [loading, setLoading] = useState(false);
  const [blikOrderId, setBlikOrderId] = useState(null);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [submittedReceipt, setSubmittedReceipt] = useState(null);
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm();
  const selectedPaymentType = watch("payment_type");
  const blikCode = watch("blik_code") || "";
  const paymentTypeInput = register("payment_type", { required: true });
  const blikCodeInput = register("blik_code", {
    validate: (value) =>
      selectedPaymentType !== "blik" ||
      /^[0-9]{6}$/.test(value || "") ||
      "Wpisz 6-cyfrowy kod BLIK",
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (!createdOrderId) reset(checkoutData);
  }, [checkoutData, reset, createdOrderId]);

  async function submit(data) {
    if (loading) return;
    if (createdOrderId && !blikOrderId) {
      history.push(`/order/${createdOrderId}`);
      return;
    }
    if (
      !createdOrderId &&
      (!canCheckout || !deliveryAvailable || cart.length === 0)
    ) {
      history.replace("/");
      return;
    }
    setLoading(true);

    if (blikOrderId) {
      try {
        await client("orders/blik/charge", {
          method: "POST",
          body: { order: blikOrderId, code: data.blik_code },
        });
        history.push(`/order/${blikOrderId}`, { thankyou: true });
      } catch (error) {
        dispatchAPIError({
          message:
            error?.json?.detail ||
            "Nie udało się rozpocząć płatności BLIK. Spróbuj ponownie.",
          show: true,
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    let order = {
      ...data,
      delivery_type: deliveryType,
      date,
      products: cart.reduce((acc, product) => {
        acc.push({
          product: product.id,
          qty: product.qty,
        });
        return acc;
      }, []),
    };
    delete order.blik_code;

    if (deliveryType === DELIVERY_ADDRESS) {
      order = {
        ...order,
        delivery_address: address,
        delivery_quote: deliveryQuote,
        delivery_address_extra: Object.entries({
          "Nr mieszkania": data.apartment_number,
          Piętro: data.floor,
          Firma: data.business,
        })
          .filter(([, v]) => typeof v === "string" && v.length > 0)
          .map(([field_name, field_value]) => `${field_name}: ${field_value}`)
          .join(", "),
      };
    } else if (deliveryType === DELIVERY_PICKUP) {
      order = { ...order, pickup_location: pickupLocation.id };
    }

    try {
      const response = await client("orders/orders/", {
        method: "POST",
        body: order,
      });
      setCreatedOrderId(response.id);
      setSubmittedReceipt({ cart, total });
      window.analytics?.track("submit_order");

      if (response.payment_type === "blik") {
        setBlikOrderId(response.id);
        dispatch(completeCheckout(data));
        try {
          await client("orders/blik/charge", {
            method: "POST",
            body: { order: response.id, code: data.blik_code },
          });
          history.push(`/order/${response.id}`, { thankyou: true });
        } catch (error) {
          history.push(`/order/${response.id}`, {
            thankyou: false,
            paymentError: error?.json?.detail,
          });
        }
        return;
      }

      // Completion is persisted synchronously before leaving for payment.
      dispatch(completeCheckout(data));

      if (response.payment_type === "przelewy24") {
        try {
          const paymentResponse = await client("orders/przelewy24", {
            method: "POST",
            body: { order: response.id },
          });
          // Router state is lost while the customer is on Przelewy24. Keep a
          // per-order marker in this tab so the confirmation survives return.
          rememberOrderThankyou(response.id);
          window.location.assign(paymentResponse.payment_url);
        } catch (_) {
          history.push(`/order/${response.id}`, {
            thankyou: true,
            paymentError: true,
          });
        }
        return;
      }

      history.push(`/order/${response.id}`, { thankyou: true });
    } catch (error) {
      const errorData = error?.json || {};
      const formFields = [
        "name",
        "phone",
        "comment",
        "payment_type",
        "pickup_at",
        "email",
      ];

      Object.entries(errorData)
        .filter(([field]) => formFields.includes(field))
        .forEach(([field, error]) => {
          setError(field, { type: "manual", message: error });
        });

      if (errorData.non_field_errors) {
        dispatchAPIError({
          message: errorData.non_field_errors[0],
          show: true,
        });
      } else if (
        !Object.keys(errorData).some((field) => formFields.includes(field))
      ) {
        dispatchAPIError({
          message: "Nie udało się złożyć zamówienia. Spróbuj ponownie.",
          show: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  if (
    !createdOrderId &&
    (cart.length === 0 || !deliveryAvailable || !menuReady)
  ) {
    return <Redirect to="/" />;
  }

  const checkoutHeader = (
    <div className="checkout-pass-header">
      <div>
        <span className="interface-label">Finalizacja</span>
        <strong>Nowe zamówienie</strong>
      </div>
      <Link className="checkout-menu-link" to="/">
        <span aria-hidden="true">←</span> Powrót do Menu
      </Link>
    </div>
  );

  const deliverySummary = (
    <div className="checkout-delivery-summary">
      <span className="interface-label">
        {deliveryType === DELIVERY_ADDRESS ? "Dostawa" : "Odbiór"}
      </span>
      <strong>
        {deliveryType === DELIVERY_ADDRESS ? address : pickupLocation?.name}
      </strong>
    </div>
  );

  const readonlyCart = (
    <OrderReceipt delivery={deliverySummary}>
      <div className="receipt-readonly" aria-label="Podsumowanie koszyka">
        <ProductsSummary
          products={submittedReceipt?.cart ?? cart}
          total={submittedReceipt?.total ?? total}
        >
          {deliveryType === DELIVERY_ADDRESS && deliveryAvailable && (
            <ProductItem>
              Dostawa
              <div>{deliveryCost} zł</div>
            </ProductItem>
          )}
        </ProductsSummary>
      </div>
    </OrderReceipt>
  );

  const paymentOptions = [
    { value: "cash", label: "Gotówka", icon: cashIcon },
    { value: "card_on_the_spot", label: "Karta", icon: cardIcon },
    { value: "przelewy24", label: "Przelewy24", icon: p24Icon },
    { value: "blik", label: "BLIK", icon: blikIcon },
  ].filter(({ value }) => paymentTypes.includes(value));

  return (
    <>
      <OrderingLayout header={checkoutHeader} receipt={readonlyCart}>
        <div className="checkout-form-zone">
          {deliveryType === DELIVERY_ADDRESS && (
            <div className="mt-4 mb-3 d-flex justify-content-between">
              <>
                <div>Dostawa na adres</div>
                <div className="text-end">{address}</div>
              </>
            </div>
          )}
          <Form onSubmit={handleSubmit(submit)}>
            {deliveryType === DELIVERY_PICKUP && (
              <Form.Group className="mb-3">
                <Row className="align-items-center">
                  <Col>
                    <div className="interface-label">Odbiór w</div>
                    <div style={{ fontSize: "21px" }}>
                      {pickupLocation?.name}
                    </div>
                  </Col>
                  <Col lg={3} xs={6}>
                    <FloatingLabel label="Godzina odbioru">
                      <Form.Control
                        type="time"
                        {...register("pickup_at", { required: true })}
                        isInvalid={errors.pickup_at}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </Form.Group>
            )}
            {deliveryType === DELIVERY_ADDRESS && (
              <Row className="mb-3">
                <Form.Group as={Col} xs={6} lg={4}>
                  <FloatingLabel label="Nr Mieszkania">
                    <Form.Control
                      type="text"
                      placeholder="Np. 21D/50"
                      {...register("apartment_number")}
                      isInvalid={errors.apartment_number}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.apartment_number &&
                        errors.apartment_number.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} xs={6} lg={4}>
                  <FloatingLabel label="Piętro">
                    <Form.Control
                      type="text"
                      {...register("floor")}
                      placeholder="Piętro"
                      isInvalid={errors.floor}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.floor && errors.floor.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} xs={12} lg={4} className="mt-3 mt-lg-0">
                  <FloatingLabel label="Firma (opcjonalnie)">
                    <Form.Control
                      type="text"
                      placeholder="Firma (opcjonalnie)"
                      {...register("business")}
                      isInvalid={errors.business}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.business && errors.business.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Row>
            )}
            <Row className="mb-3">
              <Form.Group as={Col}>
                <FloatingLabel label="Imię">
                  <Form.Control
                    type="text"
                    placeholder="Twoje imię"
                    {...register("name", {
                      required: "Musisz mieć jakieś imię",
                    })}
                    isInvalid={errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name && errors.name.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
              <Form.Group as={Col}>
                <FloatingLabel label="Telefon">
                  <Form.Control
                    type="tel"
                    placeholder="Numer kontaktowy"
                    {...register("phone", { required: "Pole wymagane" })}
                    isInvalid={errors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone && errors.phone.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <FloatingLabel label="E-mail">
                  <Form.Control
                    type="email"
                    placeholder="Wyślemy potwierdzenie zamówienia na ten adres"
                    {...register("email", { required: "Pole wymagane" })}
                    isInvalid={errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email && errors.email.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Row>
            <Form.Group className="mb-3">
              <FloatingLabel label="Komentarz">
                <Form.Control
                  as="textarea"
                  rows="3"
                  {...register("comment")}
                  placeholder="Dodatkowe wskazówki dla kierowcy lub kucharza; np. nazwa firmy, piętro, etc."
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="d-block">Sposób płatności</Form.Label>
              <div
                className={`checkout-payment-grid${
                  selectedPaymentType === "blik"
                    ? " checkout-payment-grid--blik"
                    : ""
                }`}
              >
                <SelectionTiles
                  ariaLabel="Wybierz sposób płatności"
                  type="radio"
                  options={paymentOptions}
                  value={selectedPaymentType}
                  invalid={Boolean(errors.payment_type)}
                  getInputProps={() => paymentTypeInput}
                  renderOption={({ icon, label, value }) => (
                    <>
                      {value === "blik" ? (
                        <span
                          className="checkout-blik-logo-frame"
                          aria-hidden="true"
                        >
                          <img src={icon} alt="" />
                        </span>
                      ) : (
                        <img src={icon} alt="" aria-hidden="true" />
                      )}
                      <strong>{label}</strong>
                    </>
                  )}
                />
                {selectedPaymentType === "blik" && (
                  <>
                    <input type="hidden" {...blikCodeInput} />
                    <BlikCodeInput
                      id="checkout-blik-code"
                      value={blikCode}
                      onChange={(value) =>
                        setValue("blik_code", value, {
                          shouldDirty: true,
                          shouldValidate: isSubmitted,
                        })
                      }
                      error={
                        isSubmitted ? errors.blik_code?.message : undefined
                      }
                      autoFocus
                    />
                  </>
                )}
              </div>
            </Form.Group>
            <div className="d-flex justify-content-between gap-2 align-items-center">
              <div style={{ fontSize: "0.75rem" }} className="text-muted my-1">
                Klikając ZAMAWIAM zgadzasz się na zakup zawartości koszyka i z
                poprawnością wprowadzonych danych.
              </div>
              <Button
                active
                type="submit"
                disabled={loading || (!createdOrderId && !canCheckout)}
                aria-busy={loading}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    variant="light"
                    size="sm"
                    aria-hidden="true"
                    className="me-2"
                  />
                )}
                {loading
                  ? selectedPaymentType === "przelewy24"
                    ? "Przechodzę do płatności"
                    : "Składam zamówienie"
                  : "Zamawiam"}
                {!loading && <ArrowRight size={16} className="ms-2" />}
              </Button>
            </div>
          </Form>
        </div>
      </OrderingLayout>
      <Modal open={apiError.show} className="text-center">
        <div style={{ fontSize: "55px" }}>💥</div>
        <h5>Błąd podczas składania zamówienia</h5>
        <div>{apiError.message}</div>
        <Button
          big
          onClick={() => dispatchAPIError({ show: false })}
          className="mt-4"
        >
          Zamknij
        </Button>
      </Modal>
    </>
  );
}

export default Checkout;
